// TelcoTrack — uploader + čas nahrání u fotky nedodělku.

export interface NedodelekPhotoUploaderInput {
  uploadedByUserId?: string | null;
  uploadedByName?: string | null;
  /** E-mail nahrávajícího (createdBy). Slouží jen k dohledání jména, ne jako popisek. */
  createdBy?: string | null;
  uploadedAt?: string | Date | null;
  createdAt?: string | Date | null;
}

export interface NedodelekPhotoUploaderFields {
  uploadedByUserId: string | null;
  /** Uložené jméno. Prázdné zůstává null — UI ukáže pomlčku. */
  uploadedByName: string | null;
  uploadedAt: string;
}

const PRAGUE = 'Europe/Prague';

/** Map z adresáře, nebo prostý záznam e-mail → jméno. */
type NameByEmail = ReadonlyMap<string, string> | { readonly [email: string]: string };

function lookupUploaderName(nameByEmail: NameByEmail, key: string): string | undefined {
  if (nameByEmail instanceof Map) return nameByEmail.get(key);
  const record = nameByEmail as { readonly [email: string]: string | undefined };
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

function asTrimmed(value: unknown): string {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }
  return typeof value === 'string' ? value.trim() : '';
}

function toIso(value: string | Date | null | undefined): string {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }
  const raw = asTrimmed(value);
  if (!raw) return '';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function nameFromDirectory(email: string, nameByEmail?: NameByEmail | null): string {
  if (!email || !nameByEmail) return '';
  const key = email.trim().toLowerCase();
  if (!key) return '';
  const raw = lookupUploaderName(nameByEmail, key);
  return typeof raw === 'string' ? raw.trim() : '';
}

/** Jméno pro UI. Prázdné → null (volající dosadí „—“). E-mail se nezobrazuje. */
export function nedodelekPhotoUploaderName(
  photo: NedodelekPhotoUploaderInput,
  nameByEmail?: NameByEmail | null,
): string | null {
  const stored = asTrimmed(photo.uploadedByName);
  if (stored) return stored;
  const fromDirectory = nameFromDirectory(photo.createdBy ?? '', nameByEmail);
  return fromDirectory || null;
}

/** `28. 10. 2026 12:00` v Europe/Prague. Neplatný čas → „—“. */
export function formatNedodelekPhotoUploadedAt(value: string | Date | null | undefined): string {
  const iso = toIso(value);
  if (!iso) return '—';
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PRAGUE,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const day = pick('day');
  const month = pick('month');
  const year = pick('year');
  const hour = pick('hour');
  const minute = pick('minute');
  if (!day || !month || !year || !hour || !minute) return '—';
  return `${day}. ${month}. ${year} ${hour}:${minute}`;
}

/** „Jana Nováková · 28. 10. 2026 12:00“. Prázdné jméno je pomlčka. */
export function nedodelekPhotoCaption(
  photo: NedodelekPhotoUploaderInput,
  nameByEmail?: NameByEmail | null,
): string {
  const name = nedodelekPhotoUploaderName(photo, nameByEmail) ?? '—';
  const when = formatNedodelekPhotoUploadedAt(photo.uploadedAt ?? photo.createdAt);
  return `${name} · ${when}`;
}

/** Pole, která API vrací u fotky. Prázdné jméno zůstává null. */
export function nedodelekPhotoUploaderFields(
  input: NedodelekPhotoUploaderInput,
): NedodelekPhotoUploaderFields {
  const userId = asTrimmed(input.uploadedByUserId);
  const name = asTrimmed(input.uploadedByName);
  return {
    uploadedByUserId: userId || null,
    uploadedByName: name || null,
    uploadedAt: toIso(input.uploadedAt) || toIso(input.createdAt),
  };
}
